import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, X, RefreshCw, Bot, User } from "lucide-react";

const SUGGESTIONS = [
  "Give me a weekly pipeline health check",
  "Give me the ticket ageing digest",
  "Which SCs are top performers this month?",
  "How many tickets are SLA-breached right now?",
  "What's stuck in Phase 3?",
  "Which materials are low on stock?",
];

/**
 * Floating "CRM Copilot" — Gemini 3.1 Pro powered.
 * Session persists in localStorage so refreshes keep context.
 */
export default function CrmAiAssistant() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("crm_ai_session") || null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open || !sessionId || historyLoaded) return;
    api.get(`/ai/crm/chat/${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [open, sessionId, historyLoaded]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  async function send(message) {
    const text = (message ?? draft).trim();
    if (!text || sending) return;
    setDraft("");
    const tempUser = { role: "user", content: text, ts: new Date().toISOString() };
    setMessages((p) => [...p, tempUser]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/crm/chat", { message: text, session_id: sessionId });
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("crm_ai_session", data.session_id);
      }
      setMessages((p) => [...p, { role: "assistant", content: data.answer, ts: new Date().toISOString() }]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setMessages((p) => p.slice(0, -1)); // rollback the user msg
    } finally {
      setSending(false);
    }
  }

  async function resetSession() {
    if (sessionId) {
      try { await api.delete(`/ai/crm/chat/${sessionId}`); } catch {/* silent */}
    }
    localStorage.removeItem("crm_ai_session");
    setSessionId(null);
    setMessages([]);
    setHistoryLoaded(true);
    toast.success("Started a fresh conversation");
  }

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="crm-ai-fab"
        className="fixed bottom-6 right-6 z-40 group bg-gradient-to-br from-[#1F4E2C] to-[#0F1A12] text-white rounded-full pl-3 pr-4 py-2.5 shadow-2xl hover:shadow-[0_20px_50px_-15px_rgba(31,78,44,0.6)] transition-all hover:scale-105 flex items-center gap-2"
      >
        <div className="relative">
          <Sparkles className="w-5 h-5" strokeWidth={2.5} />
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
        </div>
        <span className="font-mono text-[11px] uppercase tracking-widest">Ask AI</span>
      </button>

      {open && createPortal((
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="crm-ai-backdrop"
          />
          <div
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-[520px] bg-white shadow-2xl flex flex-col border-l border-[#DDE5D8]"
            data-testid="crm-ai-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDE5D8] bg-gradient-to-r from-[#F4F6F1] to-white">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-[#1F4E2C] text-white">
                  <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-700">Gemini 3.1 Pro</div>
                  <div className="font-display font-bold text-[#0F1A12]">CRM Copilot</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={resetSession}
                  disabled={!messages.length}
                  data-testid="crm-ai-reset"
                  className="p-2 text-[#475569] hover:text-[#1F4E2C] disabled:opacity-40"
                  title="Start a fresh conversation"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="crm-ai-close"
                  className="p-2 text-[#475569] hover:text-[#0F1A12]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-[#FFFDF8]" data-testid="crm-ai-messages">
              {messages.length === 0 && !sending && (
                <div className="text-center py-8" data-testid="crm-ai-empty">
                  <Bot className="w-10 h-10 mx-auto text-[#1F4E2C]/40" strokeWidth={1.5} />
                  <div className="font-display font-bold text-[#0F1A12] mt-3">Ask anything about the CRM</div>
                  <div className="text-xs text-[#475569] mt-1 font-mono">Grounded on live pipeline data. Try:</div>
                  <div className="mt-4 space-y-2 max-w-sm mx-auto">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        data-testid="crm-ai-suggestion"
                        className="w-full text-left text-[12px] px-3 py-2 border border-[#DDE5D8] hover:border-[#1F4E2C] hover:bg-[#F4F6F1] transition-colors rounded-sm"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`crm-ai-msg-${m.role}`}
                  className={"flex gap-2 " + (m.role === "user" ? "flex-row-reverse" : "")}
                >
                  <div className={"flex-shrink-0 p-1.5 rounded-full " + (m.role === "user" ? "bg-[#0F1A12] text-white" : "bg-[#1F4E2C] text-white")}>
                    {m.role === "user" ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                  </div>
                  <div
                    className={
                      "max-w-[85%] px-3.5 py-2 text-[13px] leading-relaxed " +
                      (m.role === "user"
                        ? "bg-[#0F1A12] text-white rounded-l-lg rounded-tr-lg"
                        : "bg-white border border-[#DDE5D8] text-[#0F1A12] rounded-r-lg rounded-tl-lg shadow-sm")
                    }
                  >
                    <MarkdownLite text={m.content} />
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-2" data-testid="crm-ai-typing">
                  <div className="p-1.5 rounded-full bg-[#1F4E2C] text-white flex-shrink-0">
                    <Sparkles className="w-3 h-3" />
                  </div>
                  <div className="bg-white border border-[#DDE5D8] rounded-r-lg rounded-tl-lg px-3.5 py-2.5 shadow-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[#1F4E2C] rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-[#1F4E2C] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#1F4E2C] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-[#DDE5D8] p-3 bg-white flex items-end gap-2"
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ask about the pipeline, tickets, SCs, or a specific opportunity…"
                data-testid="crm-ai-input"
                rows={2}
                disabled={sending}
                className="flex-1 resize-none text-sm border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none px-3 py-2 rounded-sm disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                data-testid="crm-ai-send"
                className="p-2.5 bg-[#1F4E2C] text-white hover:bg-[#0F1A12] disabled:opacity-40 rounded-sm transition-colors"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </>
      ), document.body)}
    </>
  );
}

// Very lightweight markdown-lite renderer: **bold**, • bullets, line breaks.
function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = String(text).split("\n");
  return (
    <div className="whitespace-pre-wrap break-words">
      {lines.map((ln, i) => {
        const parts = ln.split(/(\*\*[^*]+\*\*)/g);
        return (
          <div key={i}>
            {parts.map((p, j) =>
              p.startsWith("**") && p.endsWith("**")
                ? <strong key={j}>{p.slice(2, -2)}</strong>
                : <span key={j}>{p}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
